import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';

import { rectSortingStrategy } from '@dnd-kit/sortable';
import {
  Box,
  CircularProgress,
  Fade,
  Grid,
  IconButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Typography,
} from '@mui/material';
import { type GridColDef } from '@mui/x-data-grid-premium';
import { useFileDialog } from '@realadvisor/hooks';
import {
  Captions,
  CaptionsOff,
  Check,
  LayoutGrid,
  Rows3,
  Sparkles,
  Stamp,
  Trash2,
  X,
} from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';

import { FullScreenDropZone } from '@modules/_shared/components/DropZone';
import { DraggableImage } from '@modules/_shared/components/image-gallery/DraggableImage';
import { FullScreenCarousel } from '@modules/_shared/components/image-gallery/FullScreenCarousel';
import { GalleryImage } from '@modules/_shared/components/image-gallery/GalleryImage';
import { ImageUploadingCard } from '@modules/_shared/components/image-gallery/ImageUploadingCard';
import { useGalleryUploads } from '@modules/_shared/components/image-gallery/useGalleryUploads';
import { PopperMenu } from '@modules/_shared/components/PopperMenu';
import { SlideDialog } from '@modules/_shared/components/SlideDialog';
import {
  type SortableItemRenderProps,
  SortableList,
} from '@modules/_shared/components/sortable-list/SortableList';
import { SortableTable } from '@modules/_shared/components/sortable-list/SortableTable';
import type {
  ReorderEvent,
  SortableTableColDef,
} from '@modules/_shared/components/sortable-list/types';
import { UploadImageCard } from '@modules/_shared/components/UploadCard';
import { WATERMARK_SETTINGS_ANCHOR } from '@modules/_shared/components/watermark/watermarkAnchors';
import { useIsDragging } from '@modules/_shared/utils/mouseEvents';
import { useAnalytics } from '@shared/analytics';
import { ANALYTICS_EVENTS } from '@shared/analytics/constants';
import { useLocale } from '@shared/hooks/locale';

const GRID_CONTAINER_SX = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(215px, 1fr))',
  gap: 1,
  p: 1,
} as const;

export type ImageGalleryItem = {
  id: string;
  title?: string | null;
  description?: string | null;
  order_nr: number;
  watermarked_url?: string | null;
  image: {
    id: string;
    url?: string | null;
  };
  canDelete?: boolean;
  [key: string]: any;
};

type ImageGalleryProps = {
  images: ImageGalleryItem[];
  onDeleteImage?: (id: string) => void;
  onReorderImage?: (id: string, targetPosition: number) => void;
  canEditMeta?: boolean;
  onViewModeChange?: (mode: 'list' | 'grid') => void;

  viewMode?: 'list' | 'grid';
  enableViewMode?: boolean;
  disableRowSelection?: boolean;
  // Only booleans columns are supported
  extraMetaColumns?: GridColDef<ImageGalleryItem>[];
  onSelectImage?: (image: ImageGalleryItem) => void;
  onFilesUploaded?: (images: { id: string; url: string }[]) => Promise<unknown>;
  renderCustomContent?: (image: ImageGalleryItem) => React.ReactNode;
  renderRowAction?: (image: ImageGalleryItem) => React.ReactNode;
  toolbarActions?: React.ReactNode;
  showTitleAndDescription?: boolean;
  squareThumbnails?: boolean;
  showCaptions?: boolean;
  onShowCaptionsChange?: (show: boolean) => void;
  onRegenerateAllMetadata?: () => Promise<void>;
  regeneratingAll?: boolean;
  onOpenWatermarkSettings?: () => void;
  watermarkEnabled?: boolean;
};

export type NewImageGalleryRef = {
  openFullscreen: (index: number) => void;
};

export const NewImageGallery = forwardRef<
  NewImageGalleryRef,
  ImageGalleryProps
>(
  (
    {
      images,
      onDeleteImage,
      onReorderImage,
      canEditMeta = false,
      onViewModeChange,

      viewMode = 'list',
      enableViewMode = true,
      disableRowSelection = false,
      extraMetaColumns = [],
      onSelectImage,
      onFilesUploaded,
      renderCustomContent,
      renderRowAction,
      toolbarActions,
      showTitleAndDescription = false,
      squareThumbnails = false,
      showCaptions,
      onShowCaptionsChange,
      onRegenerateAllMetadata,
      regeneratingAll,
      onOpenWatermarkSettings,
      watermarkEnabled,
    },
    ref,
  ) => {
    const { t } = useLocale();
    const { track } = useAnalytics();
    const navigate = useNavigate();
    const { imgId: selectedImageId } = useParams();
    const { uploadingImages, handleFilesDrop } = useGalleryUploads({
      onFilesUploaded,
    });

    const openFileDialog = useFileDialog({
      accept: 'image/*',
      onChange: handleFilesDrop,
    });

    const enableDragAndDrop = !!onReorderImage;

    const [gridImagesEl, setGridImagesEl] = useState<HTMLElement | null>(null);
    const bottomSentinelRef = useRef<HTMLDivElement | null>(null);
    const prevImagesCountRef = useRef(images.length);
    const isDraggingImages = useIsDragging({ target: gridImagesEl });
    const [aiMenuAnchor, setAiMenuAnchor] = useState<HTMLElement | null>(null);
    const [showRegenerateDialog, setShowRegenerateDialog] = useState(false);
    const [imageToDelete, setImageToDelete] = useState<null | string>(null);
    const [fullscreenImageIndex, setFullscreenImageIndex] = useState<
      null | number
    >(null);

    useImperativeHandle(
      ref,
      () => ({
        openFullscreen: (index: number) => {
          setFullscreenImageIndex(index);
        },
      }),
      [],
    );

    useEffect(() => {
      if (
        prevImagesCountRef.current > 0 &&
        images.length > prevImagesCountRef.current
      ) {
        bottomSentinelRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'end',
        });
      }
      prevImagesCountRef.current = images.length;
    }, [images.length]);

    useEffect(() => {
      if (selectedImageId == null || gridImagesEl == null) {
        return;
      }
      const selectedRow = gridImagesEl.querySelector(
        `[data-row-id="${selectedImageId}"]`,
      );
      selectedRow?.scrollIntoView({ block: 'nearest' });
    }, [selectedImageId, gridImagesEl, viewMode, images.length]);

    const handleDelete = useCallback(
      (imageId: string) => {
        if (onDeleteImage) {
          setImageToDelete(imageId);
        }
      },
      [onDeleteImage],
    );

    const onGridRefChange = useCallback((el: HTMLElement | null) => {
      setGridImagesEl(el);
    }, []);

    const handleReorder = useCallback(
      ({ movedItem, toIndex }: ReorderEvent<ImageGalleryItem>) => {
        if (!onReorderImage) {
          return;
        }

        // targetPosition is 1-based
        onReorderImage(movedItem.id, toIndex + 1);
      },
      [onReorderImage],
    );

    const gridItemProps = useMemo(
      () => ({
        enableDragAndDrop,
        renderCustomContent,
        showTitleAndDescription,
      }),
      [enableDragAndDrop, renderCustomContent, showTitleAndDescription],
    );

    const fullScreenCarouselImages = useMemo(
      () =>
        images
          .filter(
            (img): img is typeof img & { image: { url: string } } =>
              img.image?.url != null,
          )
          .map(img => ({
            id: img.id,
            url: img.watermarked_url ?? img.image.url,
            title: img.title ?? '',
          })),
      [images],
    );

    const thumbnailWidth = squareThumbnails ? 50 : 80;

    // Build columns for SortableTable
    const listColumns = useMemo((): SortableTableColDef<ImageGalleryItem>[] => {
      const cols: SortableTableColDef<ImageGalleryItem>[] = [
        {
          field: 'thumbnail',
          headerName: '',
          width: thumbnailWidth,
          renderCell: ({ row }) => (
            <Box
              sx={{
                width: thumbnailWidth,
                height: 50,
                borderRadius: '6px',
                overflow: 'hidden',
              }}
            >
              <GalleryImage
                src={row.watermarked_url ?? row.image?.url ?? ''}
                options={{ w: thumbnailWidth, h: 50, c: 'fill', f: 'jpg' }}
                objectFit="cover"
                compact
                css={{
                  width: '100%',
                  height: '100%',
                  userSelect: 'none',
                  WebkitUserDrag: 'none',
                }}
              />
            </Box>
          ),
        },
        {
          field: 'title',
          headerName: t('Title'),
          flex: 1,
          renderCell: ({ row }) =>
            showTitleAndDescription ? (
              <Box
                sx={{
                  minWidth: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  overflow: 'hidden',
                }}
              >
                <Typography
                  variant="body2"
                  noWrap
                  sx={{
                    fontWeight: 500,
                  }}
                >
                  {row.title || t('No title')}
                </Typography>
                <Typography
                  variant="caption"
                  noWrap
                  sx={{
                    color: 'text.secondary',
                  }}
                >
                  {row.description || t('No description')}
                </Typography>
              </Box>
            ) : null,
        },
      ];

      // Add extra meta columns
      if (canEditMeta && extraMetaColumns.length > 0) {
        extraMetaColumns.forEach(column => {
          cols.push({
            field: column.field,
            headerName: column.headerName,
            width: '15%',
            align: 'center',
            renderCell: ({ row }) => {
              const val = row[column.field as keyof ImageGalleryItem];
              const isChecked =
                typeof val === 'boolean' ? (val as boolean) : false;
              return isChecked ? (
                <Box
                  component="span"
                  sx={{ display: 'inline-flex', color: 'text.secondary' }}
                >
                  <Check size={20} />
                </Box>
              ) : (
                <Box
                  component="span"
                  sx={{
                    display: 'inline-flex',
                    color: 'text.secondary',
                    opacity: 0.5,
                  }}
                >
                  <X size={20} />
                </Box>
              );
            },
          });
        });
      }

      if (renderRowAction) {
        cols.push({
          field: 'row_action',
          headerName: '',
          width: 48,
          align: 'center',
          renderCell: ({ row }) => renderRowAction(row),
        });
      }

      // Add delete column
      if (onDeleteImage) {
        cols.push({
          field: 'actions',
          headerName: '',
          width: 40,
          align: 'center',
          renderCell: ({ row }) =>
            row.canDelete !== false ? (
              <IconButton
                size="small"
                onClick={e => {
                  e.stopPropagation();
                  handleDelete(row.id);
                }}
              >
                <Trash2 size={18} />
              </IconButton>
            ) : null,
        });
      }

      return cols;
    }, [
      t,
      showTitleAndDescription,
      canEditMeta,
      extraMetaColumns,
      renderRowAction,
      thumbnailWidth,
      onDeleteImage,
      handleDelete,
    ]);

    const renderGridItem = useCallback(
      ({
        item,
        index,
        isDragOverlay,
      }: SortableItemRenderProps<ImageGalleryItem>) => (
        <DraggableImage
          key={item.id}
          image={item}
          onClick={() => onSelectImage?.(item)}
          onDelete={
            onDeleteImage != null && item.canDelete !== false
              ? () => handleDelete(item.id)
              : undefined
          }
          onFullscreen={
            isDragOverlay ? undefined : () => setFullscreenImageIndex(index)
          }
          isDragOverlay={isDragOverlay}
          {...gridItemProps}
        />
      ),
      [onSelectImage, onDeleteImage, handleDelete, gridItemProps],
    );

    return (
      <>
        {onFilesUploaded != null && (
          <FullScreenDropZone
            disabled={isDraggingImages}
            onDrop={handleFilesDrop}
          />
        )}
        <Box
          sx={{
            position: 'sticky',
            top: 0,
            left: 0,
            zIndex: 3,
            bgcolor: 'grey.100',
          }}
        >
          {onFilesUploaded != null && (
            <Box
              sx={{
                p: 1,
              }}
            >
              <UploadImageCard onClick={openFileDialog} noMinHeight />
            </Box>
          )}
          {(enableViewMode && onViewModeChange) ||
          onShowCaptionsChange ||
          toolbarActions != null ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                p: 1,
                gap: 1,
                alignItems: 'center',
                justifyContent: 'flex-start',
              }}
            >
              {enableViewMode && onViewModeChange && (
                <>
                  <IconButton
                    onClick={() => onViewModeChange('list')}
                    aria-label={t('List view')}
                  >
                    <Rows3
                      size={18}
                      strokeWidth={viewMode === 'list' ? 2.5 : 1.5}
                    />
                  </IconButton>
                  <IconButton
                    onClick={() => onViewModeChange('grid')}
                    aria-label={t('Grid view')}
                  >
                    <LayoutGrid
                      size={18}
                      strokeWidth={viewMode === 'grid' ? 2.5 : 1.5}
                    />
                  </IconButton>
                </>
              )}
              {onRegenerateAllMetadata && (
                <>
                  <IconButton
                    onClick={e => setAiMenuAnchor(e.currentTarget)}
                    disabled={regeneratingAll}
                    sx={{ marginLeft: 'auto' }}
                  >
                    {regeneratingAll ? (
                      <CircularProgress
                        size={20}
                        disableShrink
                        sx={{ color: 'grey.500' }}
                      />
                    ) : (
                      <Sparkles size={20} />
                    )}
                  </IconButton>
                  <PopperMenu
                    anchorEl={aiMenuAnchor}
                    open={Boolean(aiMenuAnchor)}
                    onClose={() => setAiMenuAnchor(null)}
                  >
                    <MenuItem
                      onClick={() => {
                        setAiMenuAnchor(null);
                        const hasExistingMetadata = images.some(
                          img => img.title ?? img.description ?? false,
                        );
                        if (hasExistingMetadata) {
                          setShowRegenerateDialog(true);
                        } else {
                          track(ANALYTICS_EVENTS.AI_GENERATE_CLICKED, {
                            source: 'gallery_regenerate_all',
                            image_count: images.length,
                          });
                          onRegenerateAllMetadata();
                        }
                      }}
                    >
                      <ListItemIcon>
                        <Sparkles size={18} />
                      </ListItemIcon>
                      <ListItemText>
                        {t('Regenerate title/description')}
                      </ListItemText>
                    </MenuItem>
                  </PopperMenu>
                </>
              )}
              {onOpenWatermarkSettings && (
                <IconButton
                  data-tour={WATERMARK_SETTINGS_ANCHOR}
                  onClick={onOpenWatermarkSettings}
                  aria-label={t('Watermark settings')}
                  color={watermarkEnabled ? 'primary' : 'default'}
                >
                  <Stamp size={20} />
                </IconButton>
              )}
              {onShowCaptionsChange && (
                <IconButton
                  onClick={() => onShowCaptionsChange(!showCaptions)}
                  aria-label={t('Toggle captions')}
                  sx={
                    onRegenerateAllMetadata ? undefined : { marginLeft: 'auto' }
                  }
                >
                  {showCaptions ? (
                    <Captions size={20} />
                  ) : (
                    <CaptionsOff size={20} />
                  )}
                </IconButton>
              )}
              {toolbarActions != null && (
                <Box sx={{ display: 'flex', marginLeft: 'auto' }}>
                  {toolbarActions}
                </Box>
              )}
            </Box>
          ) : null}
        </Box>
        {uploadingImages.length > 0 && (
          <Box
            sx={{
              p: 1,
            }}
          >
            <Grid container spacing={1}>
              {uploadingImages.map(uploadingImg => {
                // No fallback to build: the union makes an error card without its
                // rejection impossible to construct.
                const imgError =
                  uploadingImg.state === 'error'
                    ? uploadingImg.error
                    : undefined;

                return (
                  <Grid
                    key={uploadingImg.id}
                    size={{
                      xs: 4,
                      md: 3,
                      lg: 2,
                    }}
                  >
                    <Fade in>
                      <ImageUploadingCard
                        imageUrl={uploadingImg.tempUrl}
                        isLoading={uploadingImg.state === 'uploading'}
                        error={imgError}
                        height={100}
                      />
                    </Fade>
                  </Grid>
                );
              })}
            </Grid>
          </Box>
        )}
        {viewMode === 'list' && (
          <Box
            ref={onGridRefChange}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              flexGrow: 1,
              overflow: 'auto',
              height: 0,
            }}
          >
            <SortableTable
              items={images}
              columns={listColumns}
              onReorder={enableDragAndDrop ? handleReorder : undefined}
              enableDragAndDrop={enableDragAndDrop}
              onRowClick={
                disableRowSelection ? undefined : item => onSelectImage?.(item)
              }
              selectedItemId={selectedImageId ?? null}
              rowHover={!disableRowSelection}
            />
            <div ref={bottomSentinelRef} />
          </Box>
        )}
        {viewMode === 'grid' && images.length > 0 && enableDragAndDrop && (
          <>
            <SortableList
              items={images}
              onReorder={handleReorder}
              strategy={rectSortingStrategy}
              containerSx={GRID_CONTAINER_SX}
              renderItem={renderGridItem}
              activationConstraint={{ distance: 8 }}
            />
            <div ref={bottomSentinelRef} />
          </>
        )}
        {viewMode === 'grid' && images.length > 0 && !enableDragAndDrop && (
          <Box sx={GRID_CONTAINER_SX}>
            {images.map((image, index) => (
              <DraggableImage
                key={image.id}
                image={image}
                onClick={() => onSelectImage?.(image)}
                onDelete={
                  onDeleteImage != null && image.canDelete !== false
                    ? () => handleDelete(image.id)
                    : undefined
                }
                onFullscreen={() => setFullscreenImageIndex(index)}
                {...gridItemProps}
              />
            ))}
            <div ref={bottomSentinelRef} />
          </Box>
        )}
        {onRegenerateAllMetadata && (
          <SlideDialog
            open={showRegenerateDialog}
            onClose={() => setShowRegenerateDialog(false)}
            title={t('Regenerate title/description')}
            maxWidth="sm"
            fullWidth
            cancelText={t('Cancel')}
            saveText={t('Regenerate')}
            saveIcon={<Sparkles size={18} />}
            onSave={() => {
              setShowRegenerateDialog(false);
              track(ANALYTICS_EVENTS.AI_GENERATE_CLICKED, {
                source: 'gallery_regenerate_all',
                image_count: images.length,
              });
              onRegenerateAllMetadata();
            }}
          >
            <Typography sx={{ p: 2 }}>
              {t(
                'Some images already have a title or description. Regenerating will overwrite them. Do you want to continue?',
              )}
            </Typography>
          </SlideDialog>
        )}
        <SlideDialog
          open={imageToDelete != null}
          onClose={() => setImageToDelete(null)}
          title={t('Delete image')}
          maxWidth="sm"
          fullWidth
          cancelText={t('Cancel')}
          saveText={t('Delete')}
          saveColor="error"
          saveIcon={<Trash2 size={18} />}
          onSave={async () => {
            if (imageToDelete != null && onDeleteImage) {
              onDeleteImage(imageToDelete);
              if (selectedImageId === imageToDelete) {
                navigate('.', { replace: true });
              }
            }
            setImageToDelete(null);
          }}
        >
          <Typography sx={{ p: 2 }}>
            {t('Are you sure you want to delete this image?')}
          </Typography>
        </SlideDialog>
        {fullScreenCarouselImages.length > 0 && (
          <FullScreenCarousel
            images={fullScreenCarouselImages}
            viewIndex={fullscreenImageIndex ?? 0}
            open={fullscreenImageIndex != null}
            onCloseFullScreen={() => setFullscreenImageIndex(null)}
          />
        )}
      </>
    );
  },
);
